import React, { useEffect, useState, useRef } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { useParams } from "react-router-dom";
import { Card, Button, Collapse, Row, Col, Spinner } from "react-bootstrap";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { API_URL } from "..";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import "mapbox-gl/dist/mapbox-gl.css";
import { Typeahead } from "react-bootstrap-typeahead";
import { useMapbox } from "../GoogleMapsContext";

mapboxgl.accessToken = "pk.eyJ1IjoiemFtcCIsImEiOiJjbTkxa3dkcnMwMmQxMnJzYzBobW9hOHBiIn0.GWvvqPrWqtMby0HYCYdjMQ";

const getStatusColor = (status) => {
  switch (status) {
    case "Waiting": return "gray";
    case "Dispatched": return "blue";
    case "Enroute": return "orange";
    case "On Scene": return "active";
    case "Being Towed": return "red";
    case "Destination Arrival": return "purple";
    case "Completed": return "black";
    default: return "gray";
  }
};

function getYearsArray() {
  const currentYear = new Date().getFullYear();
  const startYear = 1927;
  const years = [];
  for (let year = currentYear; year >= startYear; year--) {
    years.push(`${year}`);
  }
  return years;
}

function getVehicleModelsByMake(vehicleMake) {
  const makeModelMapping = {
    Acura: ["ILX", "MDX", "NSX", "RDX", "TLX"],
    "Alfa Romeo": ["Giulia", "Stelvio", "Tonale"],
    Audi: ["A3", "A4", "A5", "A6", "Q3", "Q5", "Q7", "e-tron"],
    BMW: ["3 Series", "5 Series", "X3", "X5", "i4", "iX"],
    Chevrolet: ["Camaro", "Corvette", "Silverado", "Equinox", "Tahoe"],
    Chrysler: ["Pacifica", "300"],
    Dodge: ["Challenger", "Charger", "Durango"],
    Ford: ["Mustang", "F-150", "Bronco", "Explorer", "Mach-E"],
    GMC: ["Sierra", "Yukon", "Terrain", "Hummer EV"],
    Honda: ["Civic", "Accord", "CR-V", "Pilot"],
    Hyundai: ["Elantra", "Sonata", "Tucson", "Santa Fe", "Ioniq 5"],
    Jeep: ["Wrangler", "Grand Cherokee", "Cherokee", "Gladiator"],
    Kia: ["Forte", "Optima", "Sportage", "Telluride", "EV6"],
    Lexus: ["RX", "NX", "ES", "IS"],
    "Mercedes-Benz": ["C-Class", "E-Class", "GLC", "GLE", "EQE"],
    Nissan: ["Altima", "Sentra", "Rogue", "Pathfinder", "Leaf"],
    Porsche: ["911", "Cayenne", "Macan", "Taycan"],
    Ram: ["1500", "2500", "3500"],
    Subaru: ["Outback", "Forester", "Crosstrek"],
    Tesla: ["Model 3", "Model S", "Model X", "Model Y", "Cybertruck"],
    Toyota: ["Camry", "Corolla", "RAV4", "Highlander", "Tacoma"],
    Volkswagen: ["Jetta", "Passat", "Tiguan", "Atlas", "ID.4"],
  };
  return makeModelMapping[vehicleMake] || [];
}

function getVehicleMakes() {
  return [
    "Acura", "Alfa Romeo", "AM General", "Aston Martin", "Audi", "Bentley", "BMW",
    "Buick", "Cadillac", "Chevrolet", "Chrysler", "Dodge", "Ferrari", "Fiat",
    "Ford", "Genesis", "GMC", "Honda", "Hummer", "Hyundai", "Infiniti", "Jaguar",
    "Jeep", "Kia", "Lamborghini", "Land Rover", "Lexus", "Lincoln", "Lotus",
    "Lucid", "Maserati", "Mazda", "McLaren", "Mercedes-Benz", "Mini", "Mitsubishi",
    "Nissan", "Porsche", "Ram", "Rivian", "Rolls-Royce", "Subaru", "Tesla",
    "Toyota", "Volkswagen", "Volvo",
  ];
}

const options = [
  { value: "Light Duty Towing", label: "Light Duty Towing" },
  { value: "Medium Duty Towing", label: "Medium Duty Towing" },
  { value: "Heavy Duty Towing", label: "Heavy Duty Towing" },
  { value: "Flatbed Towing", label: "Flatbed Towing" },
  { value: "Motorcycle Towing", label: "Motorcycle Towing" },
  { value: "Long-Distance Towing", label: "Long-Distance Towing" },
  { value: "Emergency Towing", label: "Emergency Towing" },
  { value: "Accident Recovery", label: "Accident Recovery" },
  { value: "Winch-Out Services", label: "Winch-Out Services" },
  { value: "Roadside Assistance", label: "Roadside Assistance" },
  { value: "Fuel Delivery", label: "Fuel Delivery" },
  { value: "Lockout Services", label: "Lockout Services" },
  { value: "Tire Change Services", label: "Tire Change Services" },
  { value: "Jump Start Services", label: "Jump Start Services" },
  { value: "Impound Towing", label: "Impound Towing" },
  { value: "Speciality Vehicle Towing", label: "Speciality Vehicle Towing" },
  { value: "Classic Car Towing", label: "Classic Car Towing" },
  { value: "Fleet Services", label: "Fleet Services" },
  { value: "Commercial Towing", label: "Commercial Towing" },
  { value: "Recreational Vehicle Towing", label: "Recreational Vehicle Towing" },
];

function getCarColorsArray() {
  return [
    "White", "Black", "Gray", "Silver", "Blue", "Red", "Green", "Brown", "Orange",
    "Beige", "Purple", "Gold", "Yellow", "Pearl White", "Matte Black", "Metallic Gray",
    "Silver Metallic", "Navy Blue", "Bright Red", "Forest Green", "Chocolate Brown",
    "Burnt Orange", "Tan", "Deep Purple", "Champagne Gold", "Lemon Yellow",
    "Rosso Corsa", "Nardo Grey", "Ultra Red", "Blue Flame", "Podium Green",
    "Tiger Eye Pearl", "Kyalami Green", "Twilight Purple", "Jetstream II",
    "Diffused Sky Pearl", "Cacti Green", "Bronze Age", "Artisan Red", "Python Green",
    "Earl Gray", "Frozen Berry Metallic", "Sub-lime", "Hugger Orange", "Liquid Yellow",
    "Teal", "Cyan", "Magenta", "Indigo", "Maroon", "Burgundy", "Plum", "Fuchsia",
    "Aqua", "Lilac", "Sand", "Topaz", "Ash", "Charcoal", "Coral", "Amber", "Onyx",
  ];
}

function formatDate(dateString) {
  if(dateString){
    return dateString.split('.')[0];
  }
}

function formatDateToDDMMYYYY(dateString) {
  const [year, month, day] = dateString.split("-");
  return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
}

const EditJob = () => {
  const { id } = useParams();
  const { mapboxgl } = useMapbox();

  const [showAlertMessage, setshowAlertMessage] = useState({
    status: false,
    message: "",
    desc: "",
  });
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [jobType, setJobType] = useState("");
  const [callType, setCallType] = useState("New Call");
  const [priority, setPriority] = useState("");
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedOptionService, setSelectedOptionService] = useState([]);
  const [selectDriver, setSelectDriver] = useState([]);
  const [selectDriverSelect, setSelectDriverSelect] = useState({});
  const [affiliatesData, setAffilatesData] = useState([]);
  const [filterAffilatesData, setFilterAffilatesData] = useState([]);
  const [jobsData, setJobData] = useState([]);
  const [currentUser, setCurrentUser] = useState({});
  const [singleCheckedArray, setSingleCheckedArray] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const [listType, setListType] = useState("show_all");
  const [selected, setSelected] = useState([]);
  const vehicleOptions = ["Light", "Medium", "Tractor", "Equipment", "Container", "Material", "Other"];
  const [selectedYear, setSelectedYear] = useState([]);
  const VehicleYear = getYearsArray();
  const [selectedMake, setSelectedMake] = useState([]);
  const vehicleMake = getVehicleMakes();
  const [selectedModel, setSelectedModel] = useState([]);
  const [vehicleModelArray, setVehicleModelArray] = useState([]);
  const carColors = getCarColorsArray();
  const [selectedCarColor, setSelectedModelCarColor] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedPlaceDropoff, setSelectedPlaceDropoff] = useState(null);
  const [eta, setEta] = useState(null);
  const [completedDates, setCompletedDates] = useState({
    receivedDate: "",
    dispatchedDate: "",
    enRouteDate: "",
    onSceneDate: "",
    towingDate: "",
    destinationDate: "",
    completedDate: "",
  });
  const [scheduleDate, setScheduleDate] = useState("");
  const [isMounted, setIsMounted] = useState(false);
  const incidentGeocoderRef = useRef(null);
  const dropoffGeocoderRef = useRef(null);

  const [formData, setFormData] = useState({
    id: '',
    fullname: '',
    email: '',
    phone: '',
    address: '',
    zipcode: '',
    assigned_to: '',
    service_request: [],
    status: '',
    description: '',
    job_cost: '',
    assigned_by: '',
    job_type: '',
    affiliate: {},
    priority: '',
    license_plate: '',
    vehicle_type: '',
    vehicle_year: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_color: '',
    vin: '',
  });

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
      if (incidentGeocoderRef.current) {
        incidentGeocoderRef.current.onRemove();
        incidentGeocoderRef.current = null;
      }
      if (dropoffGeocoderRef.current) {
        dropoffGeocoderRef.current.onRemove();
        dropoffGeocoderRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    if (!isMounted || !mapboxgl.accessToken) return;

    const clearContainer = (id) => {
      const container = document.getElementById(id);
      if (container) container.innerHTML = '';
      return container;
    };

    const incidentContainer = clearContainer('incident-location-container');
    const dropoffContainer = clearContainer('dropoff-location-container');

    if (!incidentContainer || !dropoffContainer) return;

    incidentGeocoderRef.current = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      placeholder: formData?.address || 'Enter incident location',
      marker: false,
    });

    dropoffGeocoderRef.current = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      placeholder: formData?.dropoff_address || 'Enter dropoff location',
      marker: false,
    });

    incidentGeocoderRef.current.addTo(incidentContainer);
    dropoffGeocoderRef.current.addTo(dropoffContainer);

    incidentGeocoderRef.current.on('result', (e) => {
      const { place_name, center } = e.result;
      setSelectedPlace({
        address: place_name,
        lat: center[1],
        lng: center[0],
        name: place_name.split(',')[0]
      });
      setFormData((prev) => ({ ...prev, address: place_name }));
      if (currentUser.role === 'admin' || currentUser.role === 'user') {
        setSelectedPlace({
          address: place_name,
          lat: center[1],
          lng: center[0],
          name: place_name.split(',')[0]
        });
        getLatLongFromAddress(center[1], center[0]);
      }
    });

    dropoffGeocoderRef.current.on('result', (e) => {
      const { place_name, center } = e.result;
      const drop = {
        address: place_name,
        lat: center[1],
        lng: center[0],
        name: place_name.split(',')[0]
      };
      setSelectedPlaceDropoff(drop);
      setFormData((prev) => ({ ...prev, dropoff_address: place_name }));
      calculateETA(selectedPlace, drop);
    });

    return () => {
      if (incidentGeocoderRef.current) incidentGeocoderRef.current.onRemove();
      if (dropoffGeocoderRef.current) dropoffGeocoderRef.current.onRemove();
    };
  }, [isMounted, formData]);

  const getLatLongFromAddress = async (lat, lng) => {
    try {
      let headersList = { Accept: "*/*", "Content-Type": "application/json" };
      let bodyContent = JSON.stringify({ lat: lat, lng: lng });
      let response = await fetch(API_URL + "/api/affiliates/nearby", {
        method: "POST",
        body: bodyContent,
        headers: headersList,
      });
      let newAffiliates = await response.json();
      if (response.status === 200 && newAffiliates.length !== 0) {
        setAffilatesData(newAffiliates);
        setFilterAffilatesData(newAffiliates);
        setListType("show_all");
        
      } else {
         setAffilatesData([]);
         setFilterAffilatesData([]);
         setListType("show_all");
          setshowAlertMessage({
            message: "error",
            status: true,
            desc: "No results found for this address",
          });
        }
    } catch (error) {
      console.error("Geocoding error:", error);
      setshowAlertMessage({
        message: "error",
        status: true,
        desc: "Error finding location. Please try again.",
      });
    }
  };

  const onChangeCompany = async (selected) => {
    // setSelectDriverSelect({});
    if(selected !== undefined){

      let headersList = { Accept: "*/*" };
      try {
        console.log(typeof selected)
        const response = await fetch(`${API_URL}/api/driver/fetch/${selected}`, {
          headers: headersList,
          method: "GET",
        });
        console.log(`${API_URL}/api/driver/fetch/${selected}`)
        const data = await response.json();
        const formattedData = data.data.map((e) => ({ value: e.id, label: e.first_name + ' ' + e?.last_name }));
        setSelectDriver(formattedData);
      } catch (error) {
        console.log(error.message);
      }
    }
  };

  const handleChange = (selected) => {
    setSelectedOptions(selected);
    onChangeCompany(selected.value);
  };

  const handleChangeDriver = (selected) => {
    setSelectDriverSelect(selected || []);
  };

  const fetchJobs = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    let headersList = { Accept: "*/*", "Content-Type": "application/json" };
    let response;
    if (user.role === "owner") {
      response = await fetch(`${API_URL}/api/affiliates/fetch-affiliate/${user.id}`, {
        method: "GET",
        headers: headersList,
      });
      onChangeCompany(user.id);
      let data = await response.json();
      if (data) setJobData([{ value: data.id, label: data.business_name }]);
    } else {
      response = await fetch(API_URL + "/api/affiliates/fetch-affiliates", {
        method: "GET",
        headers: headersList,
      });
      let data = await response.json();
      const formattedData = data.map((e) => ({ value: e.id, label: e.business_name }));
      setJobData(formattedData);
    }
  };

  const fetchSingle = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    let headersList = { Accept: "*/*", "Content-Type": "application/json" };
    if (user?.role === 'admin') {
      let response = await fetch(`${API_URL}/api/jobs/${id}`, {
        method: "PUT",
        headers: headersList,
      });
      let data = await response.json();
      if (data.affiliate) setSelectedOptions({ label: data.affiliate.business_name, value: data.assigned_to });
      if (data.service_request) {
        const convert = data.service_request.split(',');
        const convertArray = convert.map((item) => ({ label: item, value: item }));
        setSelectedOptionService(convertArray);
      }
      setPhone(data?.phone);
      setFormData(data);
      setJobType(data.job_type);
      setPriority(data.priority);
      const [latitude, longitude] = data?.address_marker.split(',');
      const [latitudeDrop, longitudeDrop] = data?.dropoff_address_marker.split(',');
      setSelectedPlace({ address: data.address,lat: parseFloat(latitude) , lng: parseFloat(longitude) });
      setSelectedPlaceDropoff({ address: data.dropoff_address, lat: parseFloat(latitudeDrop) , lng: parseFloat(longitudeDrop) });
      setSelected([data.vehicle_type]);
      setSelectedYear([data.vehicle_year]);
      setSelectedMake([data.vehicle_make]);
      setVehicleModelArray(getVehicleModelsByMake(data.vehicle_make));
      setSelectedModel([data.vehicle_model]);
      setSelectedModelCarColor([data.vehicle_color]);
    } else {
      let response = await fetch(`${API_URL}/api/job/affiliate/fetch-single/${id}`, {
        method: "GET",
        headers: headersList,
      });
      let data = await response.json();
      onChangeCompany(currentUser.id);
      const [latitude, longitude] = data?.data?.address_marker.split(',');
      const [latitudeDrop, longitudeDrop] = data?.data?.dropoff_address_marker.split(',');
      if (data.data.affiliate) setSelectedOptions({ label: data.data.affiliate.business_name, value: data.data.assigned_to });
      if (data.data.service_request) {
        const convert = data.data.service_request.split(',');
        const convertArray = convert.map((item) => ({ label: item, value: item }));
        setSelectedOptionService(convertArray);
      }
      if (data.data.driver_id) setSelectDriverSelect({ value: data.data.driver.id, label: data.data.driver.first_name + ' ' + data.data.driver.last_name });
      setCallType(data.data.status_for_job);
      setCompletedDates({
        destinationDate: formatDate(data?.data?.destination_time),
        dispatchedDate: formatDate(data?.data?.dipatched_time),
        completedDate: formatDate(data?.data?.completed_time),
        enRouteDate: formatDate(data?.data?.enroute_time),
        onSceneDate: formatDate(data?.data?.onscene_time),
        towingDate: formatDate(data?.data?.towing_time),
      });
      setScheduleDate(data.data.scheduled_date);
      setPhone(data.data?.phone);
      setFormData(data.data);
      setJobType(data.data.job_type);
      setPriority(data.data.priority);
      setSelectedPlace({ address: data.data.address,lat: parseFloat(latitude) , lng: parseFloat(longitude) });
      setSelectedPlaceDropoff({ address: data.data.dropoff_address, lat: parseFloat(latitudeDrop) , lng: parseFloat(longitudeDrop) });
      setSelected([data.data.vehicle_type]);
      setSelectedYear([data.data.vehicle_year]);
      setSelectedMake([data.data.vehicle_make]);
      setVehicleModelArray(getVehicleModelsByMake(data.data.vehicle_make));
      setSelectedModel([data.data.vehicle_model]);
      setSelectedModelCarColor([data.data.vehicle_color]);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchSingle();
  }, []);

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return value;
    const formatted = [
      match[1] && `(${match[1]}`,
      match[2] && `) ${match[2]}`,
      match[3] && `-${match[3]}`,
    ].filter(Boolean).join("");
    return formatted;
  };

  const handleInputChange = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setPhone(formattedPhone);
    setFormData((prev) => ({ ...prev, phone: formattedPhone }));
  };

  const onFormSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const user = JSON.parse(localStorage.getItem('user'));
    const formattedData = selectedOptionService.map((e) => e.value);

    if (user.role === 'admin') {
      let headersList = { Accept: "*/*", "Content-Type": "application/json" };
      let bodyContent = JSON.stringify({
        fullname: formData.fullname,
        email: formData.email,
        phone: phone,
        address: selectedPlace?.address || formData.address,
        
        zipcode: formData.zipcode,
        assigned_to: selectedOptions.value,
        assigned_by: currentUser.id,
        job_cost: formData.job_cost,
        job_type: jobType,
        service_request: formattedData.toString(),
        description: formData.description,
        priority: priority,
        status_for_job: callType,
        scheduled_date: scheduleDate,
        recieved_time: completedDates.receivedDate,
        dipatched_time: completedDates.dispatchedDate,
        enroute_time: completedDates.enRouteDate,
        onscene_time: completedDates.onSceneDate,
        towing_time: completedDates.towingDate,
        destination_time: completedDates.destinationDate,
        completed_time: completedDates.completedDate,
      });

      let response = await fetch(`${API_URL}/api/jobs/update/${formData.id}`, {
        method: "PUT",
        body: bodyContent,
        headers: headersList,
      });

      let data = await response.json();
      setLoading(false);
      if (response.status === 200) {
        setshowAlertMessage({
          message: 'success',
          desc: 'You successfully updated the job information',
          status: true,
        });
      } else {
        setshowAlertMessage({
          message: 'error',
          desc: 'Please check your input fields',
          status: true,
        });
      }
    } else {
      let headersList = { Accept: "*/*", "Content-Type": "application/json" };
      let bodyContent = JSON.stringify({
        fullname: formData.fullname,
        email: formData.email,
        phone: phone,
        address: selectedPlace?.address || formData.address,
        address_marker: selectedPlace?.lat + ',' + selectedPlace.lng,
        dropoff_address: selectedPlaceDropoff.address,
        dropoff_address_marker : selectedPlaceDropoff.lat + ',' +selectedPlaceDropoff.lng,
        zipcode: formData.zipcode,
        assigned_to: currentUser.id,
        job_cost: formData.job_cost,
        priority: priority,
        driver_id: selectDriverSelect.value,
        service_request: formattedData.toString(),
        description: formData.description,
        job_type: jobType,
        license_plate: formData.license_plate,
        vehicle_type: selected[0] || formData.vehicle_type,
        vehicle_year: selectedYear[0] || formData.vehicle_year,
        vehicle_make: selectedMake[0] || formData.vehicle_make,
        vehicle_model: selectedModel[0] || formData.vehicle_model,
        vehicle_color: selectedCarColor[0] || formData.vehicle_color,
        vin: formData.vin,
        status_for_job: callType,
        scheduled_date: scheduleDate,
        recieved_time: completedDates.receivedDate,
        dipatched_time: completedDates.dispatchedDate,
        enroute_time: completedDates.enRouteDate,
        onscene_time: completedDates.onSceneDate,
        towing_time: completedDates.towingDate,
        destination_time: completedDates.destinationDate,
        completed_time: completedDates.completedDate,
      });

      let response = await fetch(`${API_URL}/api/job/affiliate/update-single/${formData.id}`, {
        method: "POST",
        body: bodyContent,
        headers: headersList,
      });

      let data = await response.json();
      setLoading(false);
      if (response.status === 200) {
        setshowAlertMessage({
          message: 'success',
          desc: 'You successfully updated the job information',
          status: true,
        });
      } else {
        setshowAlertMessage({
          message: 'error',
          desc: 'Please check your input fields',
          status: true,
        });
      }
    }
  };

  const getCurrentUser = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user == null) {
      window.location.href = "/sign-in";
    } else {
      setCurrentUser(user);
    }
  };

  useEffect(() => {
    getCurrentUser();
  }, []);

  const handleCheckboxChange = (e, index) => {
    const checked = e.target.checked;
    const affiliate = affiliatesData[index];
    if (checked) {
      setSingleCheckedArray([...singleCheckedArray, affiliate]);
    } else {
      setSingleCheckedArray(singleCheckedArray.filter((item) => item !== affiliate));
    }
  };

  const handleAssignClick = (index) => {
    const affiliate = filterAffilatesData[index];
    if (!singleCheckedArray.includes(affiliate.id)) {
      const updatedAffiliatesData = [...affiliatesData];
      updatedAffiliatesData[index].status = "marked";
      setFilterAffilatesData(updatedAffiliatesData);
      setSingleCheckedArray([...singleCheckedArray, affiliate.id]);
    }
  };

  const handleRemoveClick = (index) => {
    const affiliate = filterAffilatesData[index];
    const updatedSingleCheckedArray = singleCheckedArray.filter((id) => id !== affiliate.id);
    const updatedAffiliatesData = [...affiliatesData];
    updatedAffiliatesData[index].status = "active";
    setFilterAffilatesData(updatedAffiliatesData);
    setSingleCheckedArray(updatedSingleCheckedArray);
  };

  const toggleExpand = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    const filteredData = () => {
      if (listType === "active_only") {
        const filter = affiliatesData.filter((e) => e.status === "active" || e.status === "marked");
        setFilterAffilatesData(filter);
      } else {
        setFilterAffilatesData(affiliatesData);
      }
    };
    filteredData();
  }, [listType]);

  const handleChangeService = (selected) => {
    setSelectedOptionService(selected);
  };

  const calculateETA = async (origin, destination) => {
    try {
      if (!origin || !destination) throw new Error("Both origin and destination are required");
      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?access_token=${mapboxgl.accessToken}`
      );
      if (!response.ok) throw new Error(`Directions API error: ${response.status}`);
      const data = await response.json();
      if (data.routes && data.routes.length > 0) {
        const duration = data.routes[0].duration;
        const minutes = Math.round(duration / 60);
        setEta({
          seconds: duration,
          text: `${minutes} min`,
          distance: data.routes[0].distance
        });
      } else {
        setEta(null);
        setshowAlertMessage({
          message: "error",
          status: true,
          desc: "No route found between locations",
        });
      }
    } catch (error) {
      console.error("Error calculating ETA:", error);
      setEta(null);
      setshowAlertMessage({
        message: "error",
        status: true,
        desc: "Error calculating route. Please check locations.",
      });
    }
  };

  if (currentUser.role === "owner") {
    return (
      <MasterLayout>
        <Breadcrumb title="Edit Job" />
        
        <div className="card h-100 p-0 radius-12">
          <div className="card-body p-24">
            <div className="row justify-content-center">
              <div className="col-xxl-8 col-xl-8 col-lg-10">
                <div className="card border">
                  <div className="card-body">
                    <form onSubmit={(event) => onFormSubmit(event)}>
                      <div className="mb-20">
                        <label htmlFor="assigned_to" className="form-label fw-semibold text-primary-light text-sm mb-8">
                          This is a... <span className="text-danger-600">*</span>
                        </label>
                        <div className="d-flex align-items-center flex-wrap gap-28">
                          <div className="form-check checked-secondary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="call_type"
                              id="horizontal2211"
                              checked={callType === 'New Call'}
                              onChange={(e) => setCallType(e.target.value)}
                              value="New Call"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal2211">New Call</label>
                          </div>
                          <div className="form-check checked-secondary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="call_type"
                              id="horizontal221"
                              checked={callType === 'Completed'}
                              onChange={(e) => setCallType(e.target.value)}
                              value="Completed"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal221">Completed Call</label>
                          </div>
                          <div className="form-check checked-secondary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="call_type"
                              id="horizontal2333"
                              checked={callType === 'Schedule'}
                              onChange={(e) => setCallType(e.target.value)}
                              value="Schedule"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal2333">Schedule a call</label>
                          </div>
                          <div className="form-check checked-secondary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="call_type"
                              id="horizontal233"
                              checked={callType === 'Quote'}
                              onChange={(e) => setCallType(e.target.value)}
                              value="Quote"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal233">Quote</label>
                          </div>
                        </div>
                      </div>
                      {callType === "Completed" && (
                        <div className="row">
                          <div className="col-md-6 mt-3">
                            <label htmlFor="receivedDate" className="form-label fw-semibold text-primary-light text-sm mb-8">Received Date & Time</label>
                            <input
                              value={completedDates.receivedDate}
                              onChange={(e) => setCompletedDates((prev) => ({ ...prev, receivedDate: e.target.value }))}
                              type="datetime-local"
                              className="form-control radius-8"
                              id="receivedDate"
                              name="receivedDate"
                            />
                          </div>
                          <div className="col-md-6 mt-3">
                            <label htmlFor="dispatchedDate" className="form-label fw-semibold text-primary-light text-sm mb-8">Dispatched Date & Time</label>
                            <input
                              type="datetime-local"
                              className="form-control radius-8"
                              id="dispatchedDate"
                              name="dispatchedDate"
                              value={completedDates.dispatchedDate}
                              onChange={(e) => setCompletedDates((prev) => ({ ...prev, dispatchedDate: e.target.value }))}
                            />
                          </div>
                          <div className="col-md-6 mt-3">
                            <label htmlFor="enRouteDate" className="form-label fw-semibold text-primary-light text-sm mb-8">En route Date & Time</label>
                            <input
                              type="datetime-local"
                              className="form-control radius-8"
                              id="enRouteDate"
                              name="enRouteDate"
                              value={completedDates.enRouteDate}
                              onChange={(e) => setCompletedDates((prev) => ({ ...prev, enRouteDate: e.target.value }))}
                            />
                          </div>
                          <div className="col-md-6 mt-3">
                            <label htmlFor="onSceneDate" className="form-label fw-semibold text-primary-light text-sm mb-8">On Scene Date & Time</label>
                            <input
                              type="datetime-local"
                              className="form-control radius-8"
                              id="onSceneDate"
                              value={completedDates.onSceneDate}
                              onChange={(e) => setCompletedDates((prev) => ({ ...prev, onSceneDate: e.target.value }))}
                              name="onSceneDate"
                            />
                          </div>
                          <div className="col-md-6 mt-3">
                            <label htmlFor="towingDate" className="form-label fw-semibold text-primary-light text-sm mb-8">Towing Date & Time</label>
                            <input
                              type="datetime-local"
                              className="form-control radius-8"
                              id="towingDate"
                              name="towingDate"
                              value={completedDates.towingDate}
                              onChange={(e) => setCompletedDates((prev) => ({ ...prev, towingDate: e.target.value }))}
                            />
                          </div>
                          <div className="col-md-6 mt-3">
                            <label htmlFor="destinationDate" className="form-label fw-semibold text-primary-light text-sm mb-8">Destination Arrival Date & Time</label>
                            <input
                              type="datetime-local"
                              className="form-control radius-8"
                              id="destinationDate"
                              name="destinationDate"
                              value={completedDates.destinationDate}
                              onChange={(e) => setCompletedDates((prev) => ({ ...prev, destinationDate: e.target.value }))}
                            />
                          </div>
                          <div className="col-md-6 mt-3">
                            <label htmlFor="completedDate" className="form-label fw-semibold text-primary-light text-sm mb-8">Completed Date & Time</label>
                            <input
                              type="datetime-local"
                              className="form-control radius-8"
                              id="completedDate"
                              value={completedDates.completedDate}
                              onChange={(e) => setCompletedDates((prev) => ({ ...prev, completedDate: e.target.value }))}
                              name="completedDate"
                            />
                          </div>
                        </div>
                      )}
                      {callType === "Schedule" && (
                        <div className="row">
                          <div className="col-md-6 mt-3">
                            <label htmlFor="schedule_date" className="form-label fw-semibold text-primary-light text-sm mb-8">Schedule Date & Time</label>
                            <input
                              type="datetime-local"
                              className="form-control radius-8"
                              id="schedule_date"
                              value={scheduleDate}
                              onChange={(e) => setScheduleDate(e.target.value)}
                              name="schedule_date"
                            />
                          </div>
                        </div>
                      )}
                      <div className="mb-20">
                        <label htmlFor="assigned_to" className="form-label fw-semibold text-primary-light text-sm mb-8">Select Driver</label>
                        <Select
                          options={selectDriver}
                          value={selectDriverSelect}
                          styles={{
                            // Style the dropdown menu container
                            menu: (provided) => ({
                              ...provided,
                              zIndex: 9000, // Apply zIndex to the dropdown menu
                            }),
                            // Optional: Style the menuList if you need additional tweaks
                           
                          }}
                          onChange={handleChangeDriver}
                          placeholder="Select Options"
                        />
                      </div>
                      <div className="mb-20">
                        <div className="row">
                          <div className="col-md-6">
                            <label htmlFor="address" className="form-label fw-semibold text-primary-light text-sm mb-8">
                              Incident Location <span className="text-danger-600">*</span>
                            </label>
                            <div id="incident-location-container" key={`incident-${isMounted}`} style={{ minHeight: '50px' }} />
                            <input type="hidden" name="address" value={selectedPlace?.address || formData.address} required />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="dropoff_address" className="form-label fw-semibold text-primary-light text-sm mb-8">
                              Dropoff Location <span className="text-danger-600">*</span>
                            </label>
                            <div id="dropoff-location-container" key={`dropoff-${isMounted}`} style={{ minHeight: '50px' }} />
                            <input type="hidden" name="dropoff_address" value={selectedPlaceDropoff?.address || formData.dropoff_address} required />
                            <input type="hidden" className="form-control radius-8" id="zip_code" name="zipcode" value={formData.zipcode} onChange={(e) => setFormData((prev) => ({ ...prev, zipcode: e.target.value }))} />
                          </div>
                        </div>
                        {eta && (
                          <div className="mt-3 alert alert-info">
                            <p className="mb-0">
                              Estimated Travel Time: <strong>{eta.text}</strong> ({Math.round(eta.seconds / 60)} minutes)
                              {eta.distance && <span>, Distance: {Math.round(eta.distance / 1000)} km</span>}
                            </p>
                          </div>
                        )}
                      </div>
                      <div><h6>Customer Information</h6></div>
                      <div className="mb-20">
                        <label htmlFor="name" className="form-label fw-semibold text-primary-light text-sm mb-8">
                          Full Name <span className="text-danger-600">*</span>
                        </label>
                        <input
                          type="text"
                          name="fullname"
                          className="form-control radius-8"
                          id="name"
                          value={formData.fullname}
                          onChange={(e) => setFormData((prev) => ({ ...prev, fullname: e.target.value }))}
                          placeholder="Enter Full Name"
                        />
                      </div>
                      <div className="mb-20">
                        <div className="row">
                          <div className="col-md-6">
                            <label htmlFor="email" className="form-label fw-semibold text-primary-light text-sm mb-8">Email</label>
                            <input
                              type="email"
                              name="email"
                              className="form-control radius-8"
                              id="email"
                              value={formData.email}
                              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                              placeholder="Enter Email Address"
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="phone" className="form-label fw-semibold text-primary-light text-sm mb-8">
                              Phone <span className="text-danger-600">*</span>
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              value={phone}
                              onChange={handleInputChange}
                              className="form-control radius-8"
                              id="phone"
                              placeholder="Enter Phone Number"
                            />
                          </div>
                        </div>
                      </div>
                      <div><h6>Vehicle Information</h6></div>
                      <div className="mb-20">
                        <div className="row">
                          <div className="col-md-6">
                            <label htmlFor="license_plate" className="form-label fw-semibold text-primary-light text-sm mb-8">License Plate</label>
                            <input
                              type="text"
                              className="form-control radius-8"
                              id="license_plate"
                              name="license_plate"
                              value={formData.license_plate}
                              onChange={(e) => setFormData((prev) => ({ ...prev, license_plate: e.target.value }))}
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="vin" className="form-label fw-semibold text-primary-light text-sm mb-8">VIN</label>
                            <input
                              type="text"
                              className="form-control radius-8"
                              id="vin"
                              name="vin"
                              value={formData.vin}
                              onChange={(e) => setFormData((prev) => ({ ...prev, vin: e.target.value }))}
                            />
                          </div>
                          <div style={{ paddingTop: 10 }} />
                          <div className="col-md-4">
                            <label htmlFor="vehicle_Type" className="form-label fw-semibold text-primary-light text-sm mb-8">Vehicle Type</label>
                            <Typeahead
                              labelKey="type"
                              id="vehicle_Type"
                              options={vehicleOptions}
                              onChange={(selected) => setSelected(selected)}
                              selected={selected}
                              placeholder=""
                              inputProps={{ className: "form-control radius-8", name: "vehicle_Type" }}
                              className="radius-8"
                            />
                          </div>
                          <div className="col-md-4">
                            <label htmlFor="vehicle_Year" className="form-label fw-semibold text-primary-light text-sm mb-8">Vehicle Year</label>
                            <Typeahead
                              labelKey="name"
                              id="vehicle_Year"
                              options={VehicleYear}
                              onChange={(selected) => setSelectedYear(selected)}
                              selected={selectedYear}
                              placeholder=""
                              inputProps={{ className: "form-control radius-8", name: "vehicle_Year" }}
                              className="radius-8"
                            />
                          </div>
                          <div className="col-md-4">
                            <label htmlFor="vehicle_Make" className="form-label fw-semibold text-primary-light text-sm mb-8">Vehicle Make</label>
                            <Typeahead
                              labelKey="name"
                              id="vehicle_Make"
                              options={vehicleMake}
                              onChange={(selected) => {
                                setSelectedMake(selected);
                                setVehicleModelArray(getVehicleModelsByMake(selected[0]));
                              }}
                              selected={selectedMake}
                              placeholder=""
                              inputProps={{ className: "form-control radius-8", name: "vehicle_Make" }}
                              className="radius-8"
                            />
                          </div>
                          <div style={{ paddingTop: 10 }} />
                          <div className="col-md-4">
                            <label htmlFor="vehicle_Model" className="form-label fw-semibold text-primary-light text-sm mb-8">Vehicle Model</label>
                            <Typeahead
                              labelKey="name"
                              id="vehicle_Model"
                              options={vehicleModelArray}
                              onChange={(selected) => setSelectedModel(selected)}
                              selected={selectedModel}
                              placeholder=""
                              inputProps={{ className: "form-control radius-8", name: "vehicle_Model" }}
                              className="radius-8"
                            />
                          </div>
                          <div className="col-md-4">
                            <label htmlFor="vehicle_Color" className="form-label fw-semibold text-primary-light text-sm mb-8">Vehicle Color</label>
                            <Typeahead
                              labelKey="type"
                              id="vehicle_Color"
                              options={carColors}
                              onChange={(selected) => setSelectedModelCarColor(selected)}
                              selected={selectedCarColor}
                              placeholder=""
                              inputProps={{ className: "form-control radius-8", name: "vehicle_Color" }}
                              className="radius-8"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mb-20">
                        <div className="row">
                          <div className="col-md-6">
                            <label htmlFor="job_cost" className="form-label fw-semibold text-primary-light text-sm mb-8">Job Cost</label>
                            <input
                              type="text"
                              className="form-control radius-8"
                              id="job_cost"
                              name="job_cost"
                              value={formData.job_cost}
                              onChange={(e) => setFormData((prev) => ({ ...prev, job_cost: e.target.value }))}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mb-20">
                        <label htmlFor="assigned_to" className="form-label fw-semibold text-primary-light text-sm mb-8">
                          Priority <span className="text-danger-600">*</span>
                        </label>
                        <div className="d-flex align-items-center flex-wrap gap-28">
                          <div className="form-check checked-primary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="priority"
                              id="horizontal11"
                              checked={priority === 'normal'}
                              onChange={(e) => setPriority(e.target.value)}
                              value="normal"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal11">Normal</label>
                          </div>
                          <div className="form-check checked-secondary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="priority"
                              id="horizontal22"
                              checked={priority === 'emergency'}
                              onChange={(e) => setPriority(e.target.value)}
                              value="emergency"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal22">Emergency</label>
                          </div>
                          <div className="form-check checked-secondary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="priority"
                              id="horizontal23"
                              checked={priority === 'low'}
                              onChange={(e) => setPriority(e.target.value)}
                              value="low"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal23">Low</label>
                          </div>
                        </div>
                      </div>
                      <div className="mb-20">
                        <label htmlFor="assigned_to" className="form-label fw-semibold text-primary-light text-sm mb-8">
                          Job Type <span className="text-danger-600">*</span>
                        </label>
                        <div className="d-flex align-items-center flex-wrap gap-28">
                          <div className="form-check checked-primary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="horizontal"
                              id="horizontal1"
                              checked={jobType === 'Roadside Assistance'}
                              onChange={(e) => setJobType(e.target.value)}
                              value="Roadside Assistance"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal1">Roadside Assistance</label>
                          </div>
                          <div className="form-check checked-secondary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="horizontal"
                              id="horizontal2"
                              checked={jobType === 'Towing Service'}
                              onChange={(e) => setJobType(e.target.value)}
                              value="Towing Service"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal2">Towing Service</label>
                          </div>
                        </div>
                      </div>
                      <div className="mb-20">
                        <label htmlFor="assigned_to" className="form-label fw-semibold text-primary-light text-sm mb-8">
                          Service Required <span className="text-danger-600">*</span>
                        </label>
                        <Select
                          options={options}
                          isMulti
                          value={selectedOptionService}
                          onChange={handleChangeService}
                          placeholder="Select Services"
                        />
                      </div>
                      <div className="mb-20">
                        <label htmlFor="desc" className="form-label fw-semibold text-primary-light text-sm mb-8">Description</label>
                        <textarea
                          className="form-control radius-8"
                          id="desc"
                          placeholder="Write description..."
                          name="description"
                          value={formData.description}
                          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                        />
                      </div>
                      {showAlertMessage.status && (
                        <div
                          className={
                            showAlertMessage.message === "error"
                              ? "alert alert-danger bg-danger-50 text-danger-600 border-danger-50 px-24 py-11 mb-0 fw-semibold text-lg radius-8 d-flex align-items-center justify-content-between mb-10"
                              : "alert alert-success bg-success-50 text-success-600 border-success-50 px-24 py-11 mb-0 fw-semibold text-lg radius-8 d-flex align-items-center justify-content-between mb-10"
                          }
                          role="alert"
                        >
                          {showAlertMessage.desc}
                          <button
                            type="button"
                            onClick={() => setshowAlertMessage({ ...showAlertMessage, status: false })}
                            className="remove-button text-secondary-600 text-xxl line-height-1"
                          >
                            <Icon icon="iconamoon:sign-times-light" className="icon" />
                          </button>
                        </div>
                      )}
                      <div className="d-flex align-items-center justify-content-center gap-3">
                        {loading ? (
                          <Spinner />
                        ) : (
                          <button type="submit" className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8">Update</button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MasterLayout>
    );
  } else {
    return (
      <MasterLayout>
        <Breadcrumb title="Edit Job" />
        <div className="card h-100 p-0 radius-12">
          <div className="card-body p-24">
            <div className="row">
              <div className="col-xxl-6 col-xl-8 col-lg-10">
                <div className="card border">
                  <div className="card-body">
                    <form onSubmit={(event) => onFormSubmit(event)}>
                      <div className="mb-20">
                        <div className="row">
                          <div className="col-md-6">
                            <label htmlFor="address" className="form-label fw-semibold text-primary-light text-sm mb-8">
                              Incident Location <span className="text-danger-600">*</span>
                            </label>
                            <div id="incident-location-container" key={`incident-${isMounted}`} style={{ minHeight: '50px' }} />
                            <input type="hidden" name="address" value={selectedPlace?.address || formData.address} required />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="dropoff_address" className="form-label fw-semibold text-primary-light text-sm mb-8">
                              Dropoff Location <span className="text-danger-600">*</span>
                            </label>
                            <div id="dropoff-location-container" key={`dropoff-${isMounted}`} style={{ minHeight: '50px' }} />
                            <input type="hidden" name="dropoff_address" value={selectedPlaceDropoff?.address || formData.dropoff_address} required />
                            <input type="hidden" className="form-control radius-8" id="zip_code" name="zipcode" value={formData.zipcode} onChange={(e) => setFormData((prev) => ({ ...prev, zipcode: e.target.value }))} />
                          </div>
                        </div>
                        {eta && (
                          <div className="mt-3 alert alert-info">
                            <p className="mb-0">
                              Estimated Travel Time: <strong>{eta.text}</strong> ({Math.round(eta.seconds / 60)} minutes)
                              {eta.distance && <span>, Distance: {Math.round(eta.distance / 1000)} km</span>}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="mb-20">
                        <label htmlFor="name" className="form-label fw-semibold text-primary-light text-sm mb-8">
                          Full Name <span className="text-danger-600">*</span>
                        </label>
                        <input
                          type="text"
                          name="fullname"
                          className="form-control radius-8"
                          id="name"
                          value={formData.fullname}
                          onChange={(e) => setFormData((prev) => ({ ...prev, fullname: e.target.value }))}
                          placeholder="Enter Full Name"
                        />
                      </div>
                      <div className="mb-20">
                        <div className="row">
                          <div className="col-md-12">
                            <label htmlFor="phone" className="form-label fw-semibold text-primary-light text-sm mb-8">
                              Phone <span className="text-danger-600">*</span>
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              value={phone}
                              onChange={handleInputChange}
                              className="form-control radius-8"
                              id="phone"
                              placeholder="Enter Phone Number"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mb-20">
                        <div className="row">
                          <div className="col-md-12">
                            <label htmlFor="job_cost" className="form-label fw-semibold text-primary-light text-sm mb-8">Job Cost</label>
                            <input
                              type="text"
                              className="form-control radius-8"
                              id="job_cost"
                              name="job_cost"
                              value={formData.job_cost}
                              onChange={(e) => setFormData((prev) => ({ ...prev, job_cost: e.target.value }))}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      </div>
                      <div><h6>Vehicle Information</h6></div>
                      <div className="mb-20">
                        <div className="row">
                          <div className="col-md-6">
                            <label htmlFor="license_plate" className="form-label fw-semibold text-primary-light text-sm mb-8">License Plate</label>
                            <input
                              type="text"
                              className="form-control radius-8"
                              id="license_plate"
                              name="license_plate"
                              value={formData.license_plate}
                              onChange={(e) => setFormData((prev) => ({ ...prev, license_plate: e.target.value }))}
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="vin" className="form-label fw-semibold text-primary-light text-sm mb-8">VIN</label>
                            <input
                              type="text"
                              className="form-control radius-8"
                              id="vin"
                              name="vin"
                              value={formData.vin}
                              onChange={(e) => setFormData((prev) => ({ ...prev, vin: e.target.value }))}
                            />
                          </div>
                          <div style={{ paddingTop: 10 }} />
                          <div className="col-md-4">
                            <label htmlFor="vehicle_Type" className="form-label fw-semibold text-primary-light text-sm mb-8">Vehicle Type</label>
                            <Typeahead
                              labelKey="type"
                              id="vehicle_Type"
                              options={vehicleOptions}
                              onChange={(selected) => setSelected(selected)}
                              selected={selected}
                              placeholder=""
                              inputProps={{ className: "form-control radius-8", name: "vehicle_Type" }}
                              className="radius-8"
                            />
                          </div>
                          <div className="col-md-4">
                            <label htmlFor="vehicle_Year" className="form-label fw-semibold text-primary-light text-sm mb-8">Vehicle Year</label>
                            <Typeahead
                              labelKey="name"
                              id="vehicle_Year"
                              options={VehicleYear}
                              onChange={(selected) => setSelectedYear(selected)}
                              selected={selectedYear}
                              placeholder=""
                              inputProps={{ className: "form-control radius-8", name: "vehicle_Year" }}
                              className="radius-8"
                            />
                          </div>
                          <div className="col-md-4">
                            <label htmlFor="vehicle_Make" className="form-label fw-semibold text-primary-light text-sm mb-8">Vehicle Make</label>
                            <Typeahead
                              labelKey="name"
                              id="vehicle_Make"
                              options={vehicleMake}
                              onChange={(selected) => {
                                setSelectedMake(selected);
                                setVehicleModelArray(getVehicleModelsByMake(selected[0]));
                              }}
                              selected={selectedMake}
                              placeholder=""
                              inputProps={{ className: "form-control radius-8", name: "vehicle_Make" }}
                              className="radius-8"
                            />
                          </div>
                          <div style={{ paddingTop: 10 }} />
                          <div className="col-md-4">
                            <label htmlFor="vehicle_Model" className="form-label fw-semibold text-primary-light text-sm mb-8">Vehicle Model</label>
                            <Typeahead
                              labelKey="name"
                              id="vehicle_Model"
                              options={vehicleModelArray}
                              onChange={(selected) => setSelectedModel(selected)}
                              selected={selectedModel}
                              placeholder=""
                              inputProps={{ className: "form-control radius-8", name: "vehicle_Model" }}
                              className="radius-8"
                            />
                          </div>
                          <div className="col-md-4">
                            <label htmlFor="vehicle_Color" className="form-label fw-semibold text-primary-light text-sm mb-8">Vehicle Color</label>
                            <Typeahead
                              labelKey="type"
                              id="vehicle_Color"
                              options={carColors}
                              onChange={(selected) => setSelectedModelCarColor(selected)}
                              selected={selectedCarColor}
                              placeholder=""
                              inputProps={{ className: "form-control radius-8", name: "vehicle_Color" }}
                              className="radius-8"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mb-20">
                        <label htmlFor="assigned_to" className="form-label fw-semibold text-primary-light text-sm mb-8">
                          Priority <span className="text-danger-600">*</span>
                        </label>
                        <div className="d-flex align-items-center flex-wrap gap-28">
                          <div className="form-check checked-primary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="priority"
                              id="horizontal11"
                              checked={priority === 'normal'}
                              onChange={(e) => setPriority(e.target.value)}
                              value="Normal"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal11">Normal</label>
                          </div>
                          <div className="form-check checked-secondary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="priority"
                              id="horizontal22"
                              checked={priority === 'emergency'}
                              onChange={(e) => setPriority(e.target.value)}
                              value="emergency"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal22">Emergency</label>
                          </div>
                          <div className="form-check checked-secondary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="priority"
                              id="horizontal23"
                              checked={priority === 'low'}
                              onChange={(e) => setPriority(e.target.value)}
                              value="low"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal23">Low</label>
                          </div>
                        </div>
                      </div>
                      <div className="mb-20">
                        <label htmlFor="assigned_to" className="form-label fw-semibold text-primary-light text-sm mb-8">
                          Job Type <span className="text-danger-600">*</span>
                        </label>
                        <div className="d-flex align-items-center flex-wrap gap-28">
                          <div className="form-check checked-primary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="horizontal"
                              id="horizontal1"
                              checked={jobType === 'Roadside Assistance'}
                              onChange={(e) => setJobType(e.target.value)}
                              value="Roadside Assistance"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal1">Roadside Assistance</label>
                          </div>
                          <div className="form-check checked-secondary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="horizontal"
                              id="horizontal2"
                              checked={jobType === 'Towing Service'}
                              onChange={(e) => setJobType(e.target.value)}
                              value="Towing Service"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal2">Towing Service</label>
                          </div>
                        </div>
                      </div>
                      <div className="mb-20">
                        <label htmlFor="assigned_to" className="form-label fw-semibold text-primary-light text-sm mb-8">
                          Service Required <span className="text-danger-600">*</span>
                        </label>
                        <Select
                          options={options}
                          isMulti
                          value={selectedOptionService}
                          onChange={handleChangeService}
                          placeholder="Select Options"
                        />
                      </div>
                      <div className="mb-20">
                        <label htmlFor="desc" className="form-label fw-semibold text-primary-light text-sm mb-8">Description</label>
                        <textarea
                          className="form-control radius-8"
                          id="desc"
                          placeholder="Write description..."
                          name="description"
                          value={formData.description}
                          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                        />
                      </div>
                      {showAlertMessage.status && (
                        <div
                          className={
                            showAlertMessage.message === "error"
                              ? "alert alert-danger bg-danger-50 text-danger-600 border-danger-50 px-24 py-11 mb-0 fw-semibold text-lg radius-8 d-flex align-items-center justify-content-between mb-10"
                              : "alert alert-success bg-success-50 text-success-600 border-success-50 px-24 py-11 mb-0 fw-semibold text-lg radius-8 d-flex align-items-center justify-content-between mb-10"
                          }
                          role="alert"
                        >
                          {showAlertMessage.desc}
                          <button
                            type="button"
                            onClick={() => setshowAlertMessage({ ...showAlertMessage, status: false })}
                            className="remove-button text-secondary-600 text-xxl line-height-1"
                          >
                            <Icon icon="iconamoon:sign-times-light" className="icon" />
                          </button>
                        </div>
                      )}
                      <div className="d-flex align-items-center justify-content-center gap-3">
                        {loading ? (
                          <Spinner />
                        ) : (
                          <button type="submit" className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8">Update</button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>
              <div className="col-xxl-6 col-xl-8 col-lg-10">
                <div className="card border">
                  <div className="card-body">
                    <div className="row">
                      <div className="mb-20">
                        <label htmlFor="assigned_to" className="form-label fw-semibold text-primary-light text-sm mb-8">Affiliates By Status</label>
                        <div className="d-flex align-items-center flex-wrap gap-28">
                          <div className="form-check checked-primary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="horizontal1"
                              id="horizontal12"
                              checked={listType === "show_all"}
                              onChange={(e) => setListType(e.target.value)}
                              value="show_all"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal12">Show All</label>
                          </div>
                          <div className="form-check checked-secondary d-flex align-items-center gap-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="horizontal1"
                              id="horizontal23"
                              checked={listType === "active_only"}
                              onChange={(e) => setListType(e.target.value)}
                              value="active_only"
                            />
                            <label className="form-check-label line-height-1 fw-medium text-secondary-light" htmlFor="horizontal23">Active Only</label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div style={{ overflowY: "scroll", height: 800 }}>
                        {filterAffilatesData.map((e, index) => (
                          <div key={index} className="row">
                            <div className="col-xl-12 mb-1">
                              <Card className="radius-12 overflow-hidden d-flex align-items-center flex-nowrap flex-row">
                                <Card.Body className="p-16 flex-grow-1">
                                  <Row>
                                    <Col md="8">
                                      <input
                                        type="checkbox"
                                        checked={singleCheckedArray.includes(e.id)}
                                        onChange={(event) => handleCheckboxChange(event, index)}
                                      />
                                      <Button
                                        variant="link"
                                        style={{ textDecoration: "none" }}
                                        onClick={() => toggleExpand(index)}
                                        className="text-primary-light text-lg p-0"
                                      >
                                        {e?.business_name} {openIndex === index ? <CaretUp /> : <CaretDown />}
                                      </Button>
                                      <Collapse in={openIndex === index}>
                                        <div className="mt-2">
                                          <ul className="pl-4">
                                            {e.services_provided?.length > 0 ? (
                                              e?.services_provided.split(",").map((service, idx) => (
                                                <li key={idx} className="text-gray-700">✔ {service}</li>
                                              ))
                                            ) : (
                                              <li className="text-muted">No services available</li>
                                            )}
                                          </ul>
                                        </div>
                                      </Collapse>
                                      <br />
                                      <span>No. of Trucks: {e?.no_of_trucks}</span><br />
                                      <span>Status: {e?.status}</span><br />
                                      <span>Distance: {Math.round(parseFloat(e?.distance))} KM</span><br />
                                    </Col>
                                    <Col md="4">
                                      {e.status === "marked" ? (
                                        <Button variant="outline-danger" onClick={() => handleRemoveClick(index)} className="mt-2">Remove</Button>
                                      ) : (
                                        <Button variant="outline-success" onClick={() => handleAssignClick(index)} className="mt-2">Assign</Button>
                                      )}
                                    </Col>
                                  </Row>
                                </Card.Body>
                              </Card>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MasterLayout>
    );
  }
};

export default EditJob;